import { useState, useMemo } from 'react';
import { useRedeemGames } from '../hooks/useRedeemGames';
import { useWalletMintBalances } from '../hooks/useWalletMintBalances';
import { useRedeemAction } from '../hooks/useRedeemAction';
import RedeemTabs from '../components/redeem/RedeemTabs';
import RedeemableGamesTable from '../components/redeem/RedeemableGamesTable';
import ClosedGamesTable from '../components/redeem/ClosedGamesTable';
import RedeemModal from '../components/redeem/RedeemModal';
import './Redeem.css';

function Redeem({ walletPublicKey, walletConnected }) {
  const [activeTab, setActiveTab] = useState('redeemable');
  const [selectedGame, setSelectedGame] = useState(null);
  const { redeemableGames, closedGames, loading, error, refresh } = useRedeemGames();
  const { redeemGame, isRedeeming, redeemError } = useRedeemAction();

  // Extract all mint addresses from redeemable games
  const mintAddresses = useMemo(() => {
    return redeemableGames.map((game) => game.mint).filter(Boolean);
  }, [redeemableGames]);

  // Fetch wallet balances for all mints
  const { balances, loading: balancesLoading, refresh: refreshBalances } = useWalletMintBalances(
    walletPublicKey,
    mintAddresses
  );

  const handleOpenRedeemModal = (game) => {
    setSelectedGame(game);
  };

  const handleCloseRedeemModal = () => {
    if (!isRedeeming) {
      setSelectedGame(null);
    }
  };

  const handleConfirmRedeem = async () => {
    if (!selectedGame || !walletConnected) {
      return;
    }

    const tokenBalance = balances[selectedGame.mint] || '0';

    if (tokenBalance === '0') {
      alert('You do not own any tokens for this game.');
      return;
    }

    try {
      const signature = await redeemGame({
        gamePda: selectedGame.game_pda,
        reserveVault: selectedGame.reserve_vault,
        mint: selectedGame.mint,
        tokensIn: tokenBalance,
      });

      console.log('Redeem transaction successful:', signature);
      alert(`Redeem successful! Transaction: ${signature.slice(0, 8)}...`);

      // Refresh data after successful redeem
      await Promise.all([refresh(), refreshBalances()]);

      // Close modal
      setSelectedGame(null);
    } catch (err) {
      console.error('Redeem transaction failed:', err);
      const errorMessage = err?.message || 'Redeem transaction failed.';
      alert(`Redeem failed: ${errorMessage}`);
    }
  };

  return (
    <section className="page-section redeem-page">
      <div className="redeem-page__header">
        <div className="redeem-page__title">
          <h1>Redeem</h1>
          <p className="redeem-page__subtitle">
            Redeem your tokens from completed games or view closed games history
          </p>
        </div>

        <div className="redeem-page__info">
          <div className="redeem-info-card">
            <p className="redeem-info-card__label">Wallet Status</p>
            <p className="redeem-info-card__value">
              {walletConnected ? (
                <span className="redeem-wallet-connected">
                  Connected
                  <span className="redeem-wallet-address">
                    {walletPublicKey?.slice(0, 4)}...{walletPublicKey?.slice(-4)}
                  </span>
                </span>
              ) : (
                <span className="redeem-wallet-disconnected">Not Connected</span>
              )}
            </p>
          </div>

          <button
            type="button"
            className="redeem-refresh-button"
            onClick={refresh}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="redeem-page__explanation">
        <div className="redeem-explanation-box">
          <h3>About Redeemable Games</h3>
          <p>
            <strong>Redeemable Games</strong> are rounds that have ended and are currently in the
            redeem window. Connect your wallet to redeem any tokens you hold from these games for 
            your share of the reserve vault (SOL).
          </p>
          <p>
            <strong>Closed Games</strong> are rounds whose redeem window has ended and have been
            permanently closed. No further redemptions are possible for these games.
          </p>
          <p>
            <strong>Expired</strong> status indicates that the redeem window has passed. These games
            can no longer be redeemed.
          </p>
        </div>
      </div>

      <div className="redeem-page__content">
        <RedeemTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          redeemableCount={redeemableGames.length}
          closedCount={closedGames.length}
        />

        {loading && (
          <div className="redeem-loading">
            <p>Loading games data...</p>
          </div>
        )}

        {error && (
          <div className="redeem-error">
            <p>Error: {error}</p>
            <button type="button" onClick={refresh} className="redeem-error-retry">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {activeTab === 'redeemable' && (
              <RedeemableGamesTable
                games={redeemableGames}
                walletConnected={walletConnected}
                walletBalances={balances}
                balancesLoading={balancesLoading}
                onRedeem={handleOpenRedeemModal}
              />
            )}

            {activeTab === 'closed' && <ClosedGamesTable games={closedGames} />}
          </>
        )}
      </div>

      {selectedGame && (
        <RedeemModal
          game={selectedGame}
          tokenBalance={balances[selectedGame.mint] || '0'}
          onClose={handleCloseRedeemModal}
          onConfirm={handleConfirmRedeem}
          isProcessing={isRedeeming}
        />
      )}
    </section>
  );
}

export default Redeem;